import React, { memo, useState, useEffect, useCallback, useRef } from 'react';
import { queryProps, useSMARTGoalStepState, timeToCompleteFN, SMART_GOAL_DATA_EVENTS } from 'Components/SMARTGoal/SMARTGoalHelper';
import styles from 'assets/css/SMARTGoal.module.css';
import LoadingHandler from 'Components/LoadingWrapper';
import { useSMARTGoalPageState, useSMARTGoalPageNavigation } from 'context/SMARTGoalAppContext';
import { fetchHelper, consoleLogDev, useEventEmitter } from '@ace/react-utilities';
import { useDebouncedCallback } from 'use-debounce';
import { useMutation, useQueryClient } from 'react-query';
import { toUTCDateFromServer, toDateInputValue, toFormattedDisplayDate, getUTCDateFromServerDate, toDateISOStr, toDateSafe } from 'utils/dateUtils';
import { addDays } from 'date-fns';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import useComponentDisplayState, { ComponentToggleContextProvider, useComponentToggle } from 'context/ComponentToggleContext';
import classNames from 'classnames';
import { IconTargetArrows, IconCalendar, IconAngleDownBold } from '@ace/react-icons';


const SMARTGoalEditor = (props) => {
    const { currentStepKey } = useSMARTGoalStepState();
    const { showStep, showReview } = useSMARTGoalPageState();

    return (
        <LoadingHandler retrievalUrl={queryProps.userGoals.retrievalUrl} componentName={queryProps.userGoals.componentName} queryOpts={{ staleTime: 0, select: useCallback((data) => (data.Goals), []) }}>
            {(data) => (showStep || showReview ? <SMARTGoalTableDisplay goals={data} currentStepKey={currentStepKey} /> : false)}
        </LoadingHandler>
    );
}

export default SMARTGoalEditor;

const SMARTGoalTableDisplay = ({ goals, currentStepKey}) => {
    return (
        <ComponentToggleContextProvider componentName={queryProps.userGoals.componentName}>
            <div className={styles['sg-table__container']}>
                <ul className={styles['sg-table']}>
                    <SMARTGoalTableHeader />
                    {goals.map(goal => {
                        if (currentStepKey && goal.SMARTGoalStepKey === currentStepKey) {
                            return <SMARTGoalEditWithToggle {...goal} defaultToggleState={true} />
                        }
                        return <SMARTGoalDisplayWithToggle {...goal} currentStepKey={currentStepKey} defaultToggleState={!currentStepKey} />;
                    })}
                </ul>
            </div>
        </ComponentToggleContextProvider>
    )
};

function withToggleGoal(Component) {
    function WithToggleGoal(props) {
        const { addComponent, setComponent } = useComponentToggle(props.SMARTGoalStepKey);
        const isGoalShown = useComponentDisplayState(props.SMARTGoalStepKey);
        
        useEffect(() => {
            //console.log(`[CardContent] - OneTime addCard(toggle) call: ${props.type}`);
            addComponent(props.defaultToggleState);
        }, [props.defaultToggleState, addComponent]);

        useEffect(() => {
            setComponent(props.defaultToggleState);
        }, [props.defaultToggleState, setComponent, props.currentStepKey]);

        const classes = classNames({
            [`${styles['sg-table__row--active']}`]: isGoalShown
        });

        return (
            <Component {...props} additionalClassNames={classes} />
        );
    }

    return WithToggleGoal;
}

export const SMARTGoalStepRowEdit = ({additionalClassNames, ...props}) => {
    const {
        ShortTitle,
        StartDate,
        EndDate,
        DashboardVariantCardRetrievalID,
        DefaultDurationInDays,
        PreviousGoalEndDate
    } = props;

    const queryClient = useQueryClient();
    const [goalState, setGoalState] = useState({ StartDate: toDateSafe(StartDate), EndDate: toDateSafe(EndDate) });
    const [dateDiff, setDateDiff] = useState(null);
    const componentWillUnmount = useRef(false);
    const applyEmitterHandler = useEventEmitter(queryProps.updateUserGoal.componentName);

    const { toggleComponent } = useComponentToggle(props.SMARTGoalStepKey);

    const classes = classNames(additionalClassNames, styles["sg-table__row"], styles["sg-table__row--edit"]);

    const onDatepickerChange = useDebouncedCallback((name, val) => {
        setGoalState((state) => ({
            ...state,
            [name]: val
        }));
    }, 100);

    const getEstimatedStartDate = () => {
        if (goalState.StartDate)
            return goalState.StartDate;
        if (PreviousGoalEndDate)
            return addDays(new Date(PreviousGoalEndDate), 1);
        return new Date();
    };

    const getEstimatedEndDate = () => (addDays(getEstimatedStartDate(), DefaultDurationInDays));

    useEffect(() => {
        setDateDiff(timeToCompleteFN(goalState.StartDate, goalState.EndDate));
    }, [goalState]);

    const { mutate } = useMutation(({dataToSend}) => fetchHelper(queryProps.updateUserGoal.callbackUrl, { method: 'PUT', body: JSON.stringify(dataToSend) }), {
        onMutate: async ({dataToSend}) => {
            //cancel any pending dismiss
            await queryClient.cancelQueries(queryProps.userGoals.queryKey);

            //retrieve previous value
            const previousValue = queryClient.getQueryData(queryProps.userGoals.queryKey);

            //remove message from array of messages
            queryClient.setQueryData(queryProps.userGoals.queryKey, old => {
                let updatedGoals = old.Goals.map(x => {
                    if (x.DashboardVariantCardRetrievalID === dataToSend.DashboardVariantCardRetrievalID) {
                        x.StartDate = dataToSend.StartDate;
                        x.EndDate = dataToSend.EndDate;
                        x.Populated = x.Populated || (dataToSend.StartDate && dataToSend.EndDate);
                    }
                    return x;
                });
                return {
                    ...old,
                    Goals: updatedGoals,
                    GoalsByKey: updatedGoals.reduce((collection, instance) => { collection[instance.SMARTGoalStepKey] = instance; return collection;}, {})
                };
            });

            //return previous value
            return previousValue;
        },
        onError: (error, variables, previousValue) => {
            //Sentry.captureException(error);
            //console.error('[Message] Error occured in Message.', error);

            //set previous value if error occured (reinstate message)
            queryClient.setQueryData(queryProps.userGoals.queryKey, previousValue);
        },
        onSettled: () => {
            queryClient.invalidateQueries(queryProps.userGoals.queryKey);

            applyEmitterHandler((emitter) => {
                emitter.emit(SMART_GOAL_DATA_EVENTS.ON_UPDATE_SETTLED);
            });
        }
    });

    useEffect(() => {
        return () => {
            componentWillUnmount.current = true;
        }
    }, []);

    useEffect(() => {
        return () => {
            if (componentWillUnmount.current) {
                let dataToSend = { dataToSend: { StartDate: toDateISOStr(goalState.StartDate), EndDate: toDateISOStr(goalState.EndDate), DashboardVariantCardRetrievalID } };
                consoleLogDev('[SMARTGoalFormStep] onCleanup - Post form to server.', { ...dataToSend });
                mutate({ ...dataToSend });
            }
        }
    }, [mutate, goalState, DashboardVariantCardRetrievalID]);

    //access current state of form and get each step as a table row

    return (
        <li className={classes}>
            <div onClick={() => toggleComponent()}>
                <IconTargetArrows className={`${styles["sg-table__icon-arrows"]}`} />{ShortTitle} <IconAngleDownBold className={`${styles["sg-table__icon-angle-down"]}`} />
            </div>
            <div className={styles['sg-table__cell--timeToComplete']}>
                <span className={styles['sg-table__cell-heading']}>Time to Complete</span>
                {dateDiff && <span className={styles['sg-table__cell-value']}>{dateDiff}</span>}
            </div>
            <div className={styles['sg-table__cell--datepicker']}>
                <span className={styles['sg-table__cell-heading']}>
                    Goal Start
                </span>
                <span className={styles['sg-table__cell-value']}>
                    <IconCalendar className={styles['sg-table__icon-calendar']} fill="#5f6567" />
                    <DatePicker className={styles['sg-datepicker']} name="StartDate" selectsStart selected={goalState.StartDate} startDate={goalState.StartDate} endDate={goalState.EndDate} onChange={(date) => onDatepickerChange('StartDate', date)} openToDate={getEstimatedStartDate()} />
                </span>
            </div>
            <div className={styles['sg-table__cell--datepicker']}>
                <span className={styles['sg-table__cell-heading']}>
                    Goal End
                </span>
                <span className={styles['sg-table__cell-value']}>
                    <IconCalendar className={styles['sg-table__icon-calendar']} fill="#5f6567" />
                    <DatePicker className={styles['sg-datepicker']} name="EndDate" selectsEnd selected={goalState.EndDate} startDate={goalState.StartDate} endDate={goalState.EndDate} minDate={goalState.StartDate} onChange={(date) => onDatepickerChange('EndDate', date)} openToDate={getEstimatedEndDate()} />
                </span>
            </div>
        </li>
    )
};

export const SMARTGoalTableHeader = memo(() => (
    <li className={`${styles["sg-table__row"]} ${styles["sg-table__row--header"]}`}>
        <div>Course</div>
        <div>Time to Complete</div>
        <div>Goal Start</div>
        <div>Goal End</div>
    </li>
));

export function SMARTGoalStepRowDisplay({additionalClassNames, ...props}) {
    const { setPage } = useSMARTGoalPageNavigation();

    const {
        ShortTitle,
        StartDate,
        EndDate,
        Step
    } = props;

    const startDate = toDateSafe(StartDate);
    const endDate = toDateSafe(EndDate);
    const estimatedTime = timeToCompleteFN(startDate, endDate);

    const { toggleComponent } = useComponentToggle(props.SMARTGoalStepKey);

    const classes = classNames(additionalClassNames, styles["sg-table__row"], styles["sg-table__row--display"]);

    //onClick={() => setPage(Step + 1)}

    return (
        <li className={classes} >
            <div onClick={() => toggleComponent()}>
                <IconTargetArrows className={`${styles["sg-table__icon-arrows"]}`} />{ShortTitle} <IconAngleDownBold className={`${styles["sg-table__icon-angle-down"]}`} />
            </div>
            <div className={styles['sg-table__cell--timeToComplete']}>
                <span className={styles['sg-table__cell-heading']}>Time to Complete</span>
                <span className={styles['sg-table__cell-value']}>{estimatedTime}</span>
            </div>
            <div onClick={() => setPage(Step + 1)} className={styles['sg-table__cell--dateDisplay']}>
                <span className={styles['sg-table__cell-heading']}>
                    Goal Start
                </span>
                <span className={styles['sg-table__cell-value']}>
                    <IconCalendar className={styles['sg-table__icon-calendar']} fill="#5f6567" />
                    {toFormattedDisplayDate(startDate)}
                </span>
            </div>
            <div onClick={() => setPage(Step + 1)} className={styles['sg-table__cell--dateDisplay']}>
                <span className={styles['sg-table__cell-heading']}>
                    Goal End
                </span>
                <span className={styles['sg-table__cell-value']}>
                    <IconCalendar className={styles['sg-table__icon-calendar']} fill="#5f6567" />
                    {toFormattedDisplayDate(endDate)}
                </span>
            </div>
        </li>
    )
}

const SMARTGoalEditWithToggle = withToggleGoal(SMARTGoalStepRowEdit);
const SMARTGoalDisplayWithToggle = withToggleGoal(SMARTGoalStepRowDisplay);

